import { graphql } from '../../../../gql';
import { useModalContext } from '../../../../modal/ModalContext';
import { useApiMutation, useApiQuery } from '../../../../query/hooks';
import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSnackbar } from '../../../../store/snackbar';
import { mutationResponseHandler } from '../../../../utils/mutationResponseHandler';
import { z } from 'zod';
import { useTypedForm } from '../../../../../../shared/fe/hooks/useForm';
import { DialogContent, MenuItem, Stack, Typography } from '@mui/material';
import { ControlledFormField } from '../../../../form/ControlledFormField';
import { LoadableSelect } from '../../../../form/LoadableSelect';
import { ModalActions } from '../../../../modal/components/ModalActions';
import { UnmappedSheets } from '../MapUnmappedSheetsModal';

const handleMapping = graphql(`
	mutation mapSingleSheetToExistingMetric($input: UpdateSingleExistingContentMetricDataSet!) {
		updateContentMetricDataSetToGoogleSheet(input: $input) {
			ok
			errorMessage
		}
	}
`);

const listMetricQuery = graphql(`
	query listMetric {
		listContentMetric(
			orderBy: [{ metric: { name: asc } }]
			filter: { metric: { dataSet: { source: { type: { notEq: google_sheets } } } } }
		) {
			id
			metric {
				name
				dataSet {
					metricTemplate {
						id
					}
				}
			}
		}
	}
`);

const schema = z.object({
	metricId: z.string(),
	sheetTitle: z.string(),
});

type FormValues = z.infer<typeof schema>;

type Props = Readonly<{
	spreadsheetExternalId: string;
	isLoading: boolean;
	unmappedSheets: UnmappedSheets;
}>;

export const MapToExistingMetric: FC<Props> = ({ spreadsheetExternalId, isLoading, unmappedSheets }) => {
	const intl = useIntl();
	const { closeModal } = useModalContext();
	const { isLoading: isMetricQueryLoading, data } = useApiQuery(listMetricQuery);
	const form = useTypedForm({ schema });
	const { openSnackbar } = useSnackbar();
	const { mutateAsync } = useApiMutation(handleMapping, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.updateContentMetricDataSetToGoogleSheet.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'map-unmapped-sheets-modal.success' }),
					error: intl.formatMessage({ id: 'map-unmapped-sheets-modal.error' }),
				},
				openSnackbar,
				callback: closeModal,
			});
		},
	});

	const metrics =
		data?.listContentMetric.map((o) => ({
			label: o.metric?.name ?? '',
			id: o.id,
			metricTemplateId: o.metric?.dataSet?.metricTemplate?.id ?? '',
		})) ?? [];

	const onSubmit = async ({ metricId, sheetTitle }: FormValues) => {
		const metricTemplateId = metrics.find(({ id }) => id === metricId)?.metricTemplateId ?? '';

		await mutateAsync({ input: { spreadsheetId: spreadsheetExternalId, metricId, sheetTitle, metricTemplateId } });
	};

	return (
		<>
			<DialogContent>
				<form noValidate onSubmit={form.handleSubmit(onSubmit)}>
					<Stack gap={1.5}>
						<ControlledFormField
							form={form}
							label={intl.formatMessage({ id: 'map-unmapped-sheets-modal.metric' })}
							name={'metricId'}
							render={(props) => (
								<LoadableSelect
									{...props}
									loading={isMetricQueryLoading}
									renderValue={(value) => {
										return (
											<Typography>
												{isMetricQueryLoading ? <FormattedMessage id={'common.loading'} /> : metrics.find(({ id }) => id === value)?.label}
											</Typography>
										);
									}}
								>
									{metrics.map(({ id, label }) => (
										<MenuItem key={id} value={id}>
											{label}
										</MenuItem>
									))}
								</LoadableSelect>
							)}
						/>
						<ControlledFormField
							form={form}
							label={intl.formatMessage({ id: 'map-unmapped-sheets-modal.google-sheet' })}
							name={'sheetTitle'}
							render={(props) => (
								<LoadableSelect
									{...props}
									loading={isLoading}
									renderValue={(value) => {
										return <Typography>{isLoading ? <FormattedMessage id={'common.loading'} /> : value}</Typography>;
									}}
								>
									{unmappedSheets.map((sheet) => (
										<MenuItem key={sheet?.sheetTitle ?? ''} value={sheet?.sheetTitle ?? ''}>
											{sheet?.sheetTitle}
										</MenuItem>
									))}
								</LoadableSelect>
							)}
						/>
					</Stack>
				</form>
			</DialogContent>
			<ModalActions
				cancelButtonText={intl.formatMessage({ id: 'common.cancel' })}
				handleCancelButtonClick={closeModal}
				primaryButtonText={intl.formatMessage({ id: 'common.create' })}
				handlePrimaryButtonClick={form.handleSubmit(onSubmit)}
				disablePrimaryButton={form.formState.isSubmitting}
			/>
		</>
	);
};
